import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { fmtErr } from "@/lib/utils";

interface Props { children?: ReactNode; }
// `error` is unknown because React boundaries can receive any thrown value,
// not just Error instances (see PR #9 — the autoMenuId ReferenceError was a
// real Error, but Capacitor plugins frequently reject with plain {} objects).
interface State { hasError: boolean; error?: unknown; }

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // Use fmtErr so non-Error throws (Capacitor plain-object rejections,
    // Promise.reject() with no value, string throws) surface real detail
    // instead of logging as "{}".
    const formatted = fmtErr(error);
    const componentStack = errorInfo.componentStack ?? "<no component stack>";
    console.error("[Yumz error boundary]", formatted, "\nComponent stack:", componentStack);
    try {
      (window as any).__yumzError = {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        formatted,
        componentStack,
      };
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err instanceof Error
        ? (err.message || "Unknown error")
        : fmtErr(err);
      const stack = err instanceof Error
        ? (err.stack || "No stack trace")
        : "No stack trace";

      return (
        <div style={{
          minHeight: "100vh", display: "flex", flexDirection: "column" as const,
          alignItems: "center", justifyContent: "center", padding: 24,
          background: "white", color: "#333", fontFamily: "system-ui, sans-serif"
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ fontSize: 14, color: "#e53e3e", marginBottom: 16, maxWidth: 400, textAlign: "center" as const }}>{msg}</p>
          <pre style={{
            fontSize: 10, color: "#888", maxWidth: "90vw", overflow: "auto",
            whiteSpace: "pre-wrap" as const, wordBreak: "break-all" as const, marginBottom: 24,
            background: "#f5f5f5", padding: 12, borderRadius: 8, maxHeight: 200
          }}>{stack}</pre>
          <Button
            onClick={() => {
              this.setState({ hasError: false });
              window.location.href = "/";
            }}
          >
            go home
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
